import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {object} MobileVariantPickerDialogRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} body - The dialog body element.
 * @property {HTMLButtonElement} [applyButton] - The apply button element.
 */

/**
 * Mobile Variant Picker Dialog Component.
 * Displays a full-screen mobile dialog with the desktop variant picker inside.
 * The dialog only handles opening/closing, variant selection is handled by the embedded variant-picker.
 *
 * @extends Component<MobileVariantPickerDialogRefs>
 */
class MobileVariantPickerDialog extends Component {
  requiredRefs = ['dialog'];

  /** @type {number} */
  #previousScrollY = 0;

  /**
   * Opens the dialog.
   * Uses the same desktop :has(:checked) CSS for variant styling.
   * Includes an iOS Safari workaround: hides body content, toggles radio states
   * synchronously after showModal() to force :has(:checked) re-evaluation,
   * then reveals content — all within a single rAF, before the browser paints.
   */
  showDialog = () => {
    const { dialog, body } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent iOS Safari from painting broken :has(:checked) state
    if (body) body.style.visibility = 'hidden';

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();

      // iOS Safari: Force :has(:checked) CSS selector re-evaluation.
      // Safari may skip evaluating :has() on dialog content at first render.
      // Toggling radio checked state + forcing synchronous reflow invalidates
      // the cached selector results so the desktop variant styles render correctly.
      const wrapper = this.querySelector('.mobile-variant-picker-dialog__variant-picker-wrapper');
      if (wrapper) {
        const checkedInputs = /** @type {HTMLInputElement[]} */ (
          [...wrapper.querySelectorAll('input[type="radio"]:checked')]
        );
        checkedInputs.forEach((input) => { input.checked = false; });
        void wrapper.offsetHeight;
        checkedInputs.forEach((input) => { input.checked = true; });
        void wrapper.offsetHeight;
      }

      // Reveal content after :has(:checked) is properly evaluated
      if (body) body.style.visibility = '';
    });
  };

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
  };

  /**
   * Applies changes and closes the dialog.
   */
  applyChanges = () => {
    this.closeDialog();
  };
}

if (!customElements.get('mobile-variant-picker-dialog')) {
  customElements.define('mobile-variant-picker-dialog', MobileVariantPickerDialog);
}

/**
 * Initialize trigger buttons for mobile variant picker dialogs.
 */
(() => {
  document.addEventListener('DOMContentLoaded', () => {
    const triggers = document.querySelectorAll('[data-dialog-trigger]');
    
    triggers.forEach((trigger) => {
      if (!(trigger instanceof HTMLButtonElement)) return;
      
      trigger.addEventListener('click', () => {
        const dialogId = trigger.dataset.dialogId;
        if (!dialogId) return;
        
        const dialogWrapper = document.getElementById(dialogId);
        const dialogComponent = dialogWrapper?.querySelector('mobile-variant-picker-dialog');
        
        if (dialogComponent instanceof MobileVariantPickerDialog) {
          dialogComponent.showDialog();
        }
      });
    });
  });
})();
